import { Project } from '../../types/project';
import { canRunTraditional } from './runTraditional';

export type ReportMethod = 'measured' | 'traditional';

/** The measured path can produce a result: a nonzero interval series or an
 * entered peak demand. */
export function canRunMeasured(
  project: Partial<Project>,
  amiData: ReadonlyArray<{ kw: number }>
): boolean {
  return (
    (amiData.length > 0 && amiData.some((d) => d.kw > 0)) ||
    (typeof project.max_recorded_demand_kw === 'number' && project.max_recorded_demand_kw > 0)
  );
}

/**
 * The method the report is actually built on. Results, the report, and the
 * completion checklist all ask this one question, so they cannot disagree.
 *
 * The stored report_method wins while its path can run. A stored basis whose
 * path cannot run yields to the one that can: a contractor who clicked
 * Traditional before the meter data arrived, typed a few items, then finished
 * the measured path was blocked by a traditional checklist for a report that
 * was printing measured (2026-10-16). With neither path runnable the stored
 * choice stands, so its own checklist says what is missing.
 */
export function effectiveReportMethod(
  project: Partial<Project>,
  amiData: ReadonlyArray<{ kw: number }>
): ReportMethod {
  const measured = canRunMeasured(project, amiData);
  const traditional = canRunTraditional(project);
  const stored = project.report_method;
  if (stored === 'traditional' && traditional) return 'traditional';
  if (stored === 'measured' && measured) return 'measured';
  if (measured) return 'measured';
  if (traditional) return 'traditional';
  return stored === 'traditional' ? 'traditional' : 'measured';
}

/**
 * The report_method to persist, or undefined when the stored value is right.
 * Writes only when exactly one path can run and the stored value disagrees,
 * or nothing is stored. With both paths live and nothing stored, the Results
 * picker makes the choice explicit; with both live and a choice stored, the
 * choice stands.
 */
export function reportMethodCorrection(
  project: Partial<Project>,
  amiData: ReadonlyArray<{ kw: number }>
): ReportMethod | undefined {
  const measured = canRunMeasured(project, amiData);
  const traditional = canRunTraditional(project);
  if (measured === traditional) return undefined;
  const runnable: ReportMethod = measured ? 'measured' : 'traditional';
  return project.report_method === runnable ? undefined : runnable;
}

/** Every project field that only the traditional path reads, at its
 * payload default (projectPayload.ts). Applied when a contractor abandons the
 * traditional path; the dwelling-inventory rows go with it, removed rows stay
 * (the measured path lists and credits them). */
export const TRADITIONAL_INPUT_RESET: Partial<Project> = {
  dwelling_floor_area_sqft: null,
  added_floor_area_sqft: null,
  small_appliance_circuit_count: 2,
  laundry_circuit_count: 1,
  occupancy_type: null,
  inventory_attested: false,
  traditional_submethod: 'auto',
  noncoincident_election: false,
  evse_demand_policy: 'pooled',
  apply_evse_floor: true,
  override_tier_kva: null,
  override_tier_percent: null,
  override_remainder_percent: null,
  override_evse_percent: null,
};

/** The project has anything on the traditional path worth clearing. */
export function hasTraditionalEntries(
  project: Partial<Project>,
  inventoryRowCount: number
): boolean {
  return (
    inventoryRowCount > 0 ||
    project.dwelling_floor_area_sqft != null ||
    project.added_floor_area_sqft != null ||
    project.occupancy_type != null ||
    project.inventory_attested === true ||
    project.report_method === 'traditional'
  );
}
